/**
 * Display formatters used across pages. All inputs are strings (Decimal
 * or bigint, JSON-encoded) so the formatters don't depend on heavy VOs
 * on the client.
 */

const USD = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

const COMPACT = new Intl.NumberFormat("en-US", {
	notation: "compact",
	maximumFractionDigits: 2,
});

/** USDC raw (6-decimal bigint as string) → human "$1,234.56". */
export function formatUsdcRaw(raw: string | number | bigint): string {
	const bi = typeof raw === "bigint" ? raw : BigInt(raw);
	const sign = bi < 0n ? -1n : 1n;
	const abs = sign === -1n ? -bi : bi;
	const whole = abs / 1_000_000n;
	const cents = (abs % 1_000_000n) / 10_000n;
	const value = Number(whole) + Number(cents) / 100;
	return USD.format(sign === -1n ? -value : value);
}

/** Decimal-string fraction → "12.34%". */
export function formatPercent(value: string | number, digits = 2): string {
	const n = typeof value === "number" ? value : Number(value);
	if (!Number.isFinite(n)) return "—";
	return `${(n * 100).toFixed(digits)}%`;
}

/** Pretty-print a weight (0..1) as "12.3%" using percent helper. */
export function formatWeight(value: string | number): string {
	return formatPercent(value, 2);
}

/** Compact a big USDC raw for badges / KPIs. */
export function formatUsdcCompact(raw: string | bigint): string {
	const bi = typeof raw === "bigint" ? raw : BigInt(raw);
	const usd = Number(bi) / 1_000_000;
	return `$${COMPACT.format(usd)}`;
}

/** Right-pad an asset id for column alignment in mono fonts. */
export function shortAssetId(id: string, max = 28): string {
	return id.length <= max ? id : `${id.slice(0, max - 1)}…`;
}

/** 0x-prefixed address → "0xabcd…1234". Empty/null → "—". */
export function formatAddress(addr: string | null | undefined): string {
	if (!addr) return "—";
	return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/**
 * Format a token raw amount (bigint string) using its decimals. Works
 * for arbitrary decimals (6 USDC, 18 USDe, 8 WBTC). Truncates fractional
 * places to `display` digits for compactness.
 */
export function formatTokenAmount(
	raw: string | bigint,
	decimals: number,
	display = 2,
): string {
	const bi = typeof raw === "bigint" ? raw : BigInt(raw);
	if (bi === 0n) return "0";
	const sign = bi < 0n ? "-" : "";
	const abs = sign === "-" ? -bi : bi;
	const base = 10n ** BigInt(decimals);
	const whole = abs / base;
	const fracBase = 10n ** BigInt(Math.min(display, decimals));
	const fracRaw =
		decimals > display
			? abs / 10n ** BigInt(decimals - display) - whole * fracBase
			: abs - whole * base;
	const wholeStr = whole.toLocaleString("en-US");
	if (fracRaw === 0n) return `${sign}${wholeStr}`;
	const fracStr = fracRaw.toString().padStart(Math.min(display, decimals), "0");
	return `${sign}${wholeStr}.${fracStr}`;
}
